import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { AbstractAppConfig } from '../../../app.config';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';

/**
 * `Oauth2Service` and `AuthService` cannot be merged as it creates a cyclic
 * dependency on `AuthService` through `HttpErrorService`.
 */
@Injectable()
export class AuthService {

    private static readonly VAULT_TTL_LEEWAY = 5000; // milliseconds
    private static readonly ACCESS_TOKEN = 'access_token';
    private static readonly ID_TOKEN = 'id_token';

    private tokensVault = (() => {
        const vault = {};
        const get = (key) => vault[key]?.expiresAt > Date.now() + AuthService.VAULT_TTL_LEEWAY ? vault[key].value : undefined;
        const set = (key) => (value, expiresAt) => (vault[key] = {value, expiresAt}) && value;
        return {get, set};
    })();

    constructor(private appConfig: AbstractAppConfig,
                private httpClient: HttpClient,
                @Inject(DOCUMENT) private document: any) {
    }

    public signIn(): void {
        const loginUrl = this.appConfig.getLoginUrl().replace(/\{\{(\w+)\}\}/g, (_, key) => ({
            client_id: this.appConfig.getOAuth2ClientId(),
            redirect_uri: encodeURIComponent(this.appConfig.getRedirectUri()),
        }[key]));

        this.document.location.href = loginUrl;
    }

    public signOut(): void {
        const logoutUrl = this.appConfig.getSsoLogoutUrl().replace(/\{\{(\w+)\}\}/g, (_, key) => ({
            redirect_uri: encodeURIComponent(this.document.location.origin),
            id_token: this.getIdToken(),
        }[key]));

        this.document.location.href = logoutUrl;
    }

    public getAccessToken(): string  {
        return this.tokensVault.get(AuthService.ACCESS_TOKEN);
    }

    public getIdToken(): string {
        return this.tokensVault.get(AuthService.ID_TOKEN);
    }

    public renewTokens(code?) {
        if (this.getAccessToken() && this.getIdToken()) {
          return Promise.resolve();
        }
        const url = `${window.location.origin}/oauth2/tokens`;
        return this.httpClient
            .get(url, {params: {...(code && {code})}})
            .pipe(
                catchError((error: any): any => {
                    return throwError(error || 'Server error');
                }),
                map(({access_token, id_token, expires_in}) => {
                    const expires_at =  Date.now() + expires_in * 1000;
                    this.tokensVault.set(AuthService.ACCESS_TOKEN)(access_token, expires_at);
                    this.tokensVault.set(AuthService.ID_TOKEN)(id_token, Date.now() + 999999 * 1000);
                }))
            .toPromise();
    };

    public hasSession = async () => this.renewTokens()
        .then(() => true)
        .catch(() => false);
}
